import type { CustomInstrumentInfo } from "@repo/shared/types";
import { useState } from "react";
import { toast } from "sonner";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { apiErrorMessage } from "@/lib/api/_client";
import * as customInstrumentsApi from "@/lib/api/custom-instruments";
import { useDeleteCustomInstrument } from "@/lib/hooks/use-custom-instruments";
import { useT } from "@/lib/i18n/use-t";
import { CustomInstrumentChartDialog } from "./custom-instrument-chart-dialog";
import { CustomInstrumentTable } from "./custom-instruments-table";
import { T } from "./custom-instruments-wrapper.i18n";
import { ManageCustomInstumentDialog } from "./manage-custom-instrument-dialog";

type CustomInstrumentsWrapperProps = {
  items: CustomInstrumentInfo[];
};

export function CustomInstrumentsWrapper({ items }: CustomInstrumentsWrapperProps) {
  const t = useT(T);
  const deleteMutation = useDeleteCustomInstrument();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTicker, setEditingTicker] = useState<string | null>(null);

  const [chartDialogOpen, setChartDialogOpen] = useState(false);
  const [chartTicker, setChartTicker] = useState<string | null>(null);
  const [chartCurrency, setChartCurrency] = useState<string | null>(null);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [tickerToDelete, setTickerToDelete] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  const handleCreate = () => {
    setEditingTicker(null);
    setDialogOpen(true);
  };

  const handleEdit = (item: CustomInstrumentInfo) => {
    setEditingTicker(item.ticker);
    setDialogOpen(true);
  };

  const handleNameClick = (item: CustomInstrumentInfo) => {
    setChartTicker(item.ticker);
    setChartCurrency(item.currency);
    setChartDialogOpen(true);
  };

  const handleDeleteClick = async (item: CustomInstrumentInfo) => {
    try {
      const usage = await customInstrumentsApi.getUsage(item.ticker);
      if (!usage.canDelete) {
        const parts = [t.instrumentInUse];
        if (usage.combinedInstruments.length > 0) {
          parts.push(t.usedByCombined + usage.combinedInstruments.join(", "));
        }
        if (usage.products.length > 0) {
          parts.push(t.usedByProducts + usage.products.join(", "));
        }
        showAlert(t.error, parts.join("\n"), "destructive");
        return;
      }
      setTickerToDelete(item.ticker);
      setConfirmDeleteOpen(true);
    } catch (err) {
      showAlert(t.error, apiErrorMessage(err, t.failedToDeleteInstrument), "destructive");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!tickerToDelete) return;
    try {
      await deleteMutation.mutateAsync(tickerToDelete);
      toast.success(t.instrumentDeletedSuccess);
    } catch (err) {
      showAlert(t.error, apiErrorMessage(err, t.failedToDeleteInstrument), "destructive");
    }
    setTickerToDelete(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingTicker(null);
  };

  const handleChartDialogClose = () => {
    setChartDialogOpen(false);
    setChartTicker(null);
    setChartCurrency(null);
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={handleCreate}>{t.addCustomInstument}</Button>
      </div>
      <ManageCustomInstumentDialog
        open={dialogOpen}
        editingTicker={editingTicker}
        onClose={handleDialogClose}
      />
      <CustomInstrumentChartDialog
        open={chartDialogOpen}
        ticker={chartTicker}
        currency={chartCurrency}
        onClose={handleChartDialogClose}
      />
      <CustomInstrumentTable
        items={items}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        onNameClick={handleNameClick}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteInstrument}
        description={t.deleteInstrumentDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDeleteConfirm}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
